import { skipToken } from '@reduxjs/toolkit/query/react'
import { ClientUser, UserRole } from '@numeo/types-core'
import { useMemo } from 'react'
import { useAppSelector } from '@/hooks'
import { useGetTeamDriversFullQuery } from '@/store/api/teamApi'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

/** The most drivers one `view=full` request may name (server limit). */
const TEAM_MAX_FULL_IDS = 20

interface TeamDriversFullState {
    /** The records found, in no particular order; a driver the caller may not see is simply absent. */
    drivers: ClientUser[]
    isLoading: boolean
    isError: boolean
}

const NONE: ClientUser[] = []

/**
 * The whole record of a few drivers (at most 20), for the driver form and the screens that edit a driver:
 * `GET /team/drivers/v1?view=full&ids=` (DRIVER_PROFILES:VIEW), cached five minutes. Before the directory serves the
 * company, the same records from `application.users`.
 */
export const useTeamDriversFull = (ids: readonly (string | null | undefined)[], options: { skip?: boolean } = {}): TeamDriversFullState => {
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = [...new Set(ids.filter((id): id is string => !!id).map(String))].sort().slice(0, TEAM_MAX_FULL_IDS).join(',')
    const wanted = useMemo(() => (key ? key.split(',') : []), [key])
    const skip = !!options.skip || !wanted.length

    const remote = useGetTeamDriversFullQuery(enabled && applicationId && !skip ? { applicationId, ids: wanted } : skipToken)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))
    const local = useMemo(() => {
        if (enabled || skip) return NONE
        const set = new Set(wanted)
        return (users ?? []).filter((user) => user.role === UserRole.DRIVER && set.has(String(user.id)))
    }, [enabled, skip, users, wanted])

    if (skip) return { drivers: NONE, isLoading: false, isError: false }
    if (!enabled) return { drivers: local, isLoading: false, isError: false }
    return { drivers: remote.data ?? NONE, isLoading: remote.isLoading, isError: remote.isError }
}
